import { browserTracingIntegration, init, replayIntegration, setTags, setUser } from "@sentry/react";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useUserContext } from "lib/contexts/UserProvider";
import { useEffect, useState } from "react";

const useSentry = () => {
  const [isSentryLoaded, setIsSentryLoaded] = useState(false);
  const {
    state: { merchant },
  } = useMerchantContext();
  const {
    state: { user },
  } = useUserContext();

  const isLoggingEnabled = merchant?.isDebugLoggingEnabled ?? false;

  useEffect(() => {
    const dsn = import.meta.env.VITE_SENTRY_DNS;
    let replaysSessionSampleRate = 1.0;
    let enabled = false;
    if (["production"].includes(import.meta.env.VITE_NODE_ENV)) {
      replaysSessionSampleRate = 0.1;
      enabled = true;
    }

    if (dsn && !isSentryLoaded && enabled) {
      init({
        dsn,
        enableLogs: isLoggingEnabled,
        // release: sentryConfig.release, //Todo: add a release version
        environment: import.meta.env.VITE_NODE_ENV,
        integrations: [
          browserTracingIntegration(),
          ...(import.meta.env.MODE === "production" ? [replayIntegration()] : []),
        ],
        tracesSampler(samplingContext) {
          if (samplingContext.parentSampled !== undefined) {
            return samplingContext.parentSampled;
          }

          return 0.1;
        },
        replaysSessionSampleRate,
        replaysOnErrorSampleRate: 1.0,
        ignoreErrors: [
          /^Clarity/,
          // case of no network on user's device
          /TypeError: Network request failed/,
          // /ChunkLoadError/,
          /**
           * safe to ignore as these are
           * https://stackoverflow.com/questions/71280168/javascript-typeerror-load-failed-error-when-calling-fetch-on-ios
           */
          /Load failed/,
          /TypeError: Failed to fetch dynamically imported module/,
          /TypeError: Importing a module script failed/,
          /TypeError: error loading dynamically imported module/,
          /TypeError: cancelled/,
          /The string did not match the expected pattern/,
          /is not a valid JavaScript MIME type/,
          /Can't find variable: clearClarity/,
        ],
        beforeSend(event, hint) {
          // Check if the error is from Clarity based on the stack trace
          if (
            event?.exception?.values?.some((exception) =>
              exception?.stacktrace?.frames?.some(
                (frame) => frame?.filename?.includes("clarity.ms") || frame?.filename?.includes("clarity.js"),
              ),
            )
          ) {
            return null; // Drop the event
          }

          const error = hint?.originalException;
          if (error && typeof error === "object" && "isAxiosError" in error && error.isAxiosError) {
            const axiosError = error as any;

            // Drop AxiosErrors with no response status code (network errors, canceled requests)
            if (!axiosError.response?.status) {
              return null;
            }
            // Override error title with API error message
            const apiError = axiosError.response?.data?.error;

            // Drop known non-actionable API errors
            const excludedApiErrors = ["Checkout already completed", "Checkout already finished"];
            if (
              apiError?.message?.toLowerCase() &&
              excludedApiErrors.some((msg) => apiError.message?.toLowerCase()?.includes(msg.toLowerCase()))
            ) {
              return null;
            }

            if (apiError?.message && event.exception?.values?.length) {
              const lastException = event.exception.values[event.exception.values.length - 1];
              lastException.value = `${axiosError.response.status} ${axiosError.config?.url ?? ""} - ${
                apiError.code ? `${apiError.code}: ` : ""
              }${apiError.message}`;
            }

            // Add request details as extra context
            if (axiosError.config) {
              event.contexts = {
                ...event.contexts,
                axios: {
                  url: axiosError.config.url,
                  method: axiosError.config.method,
                  baseURL: axiosError.config.baseURL,
                  headers: axiosError.config.headers,
                },
              };

              // Also add response details if available
              if (axiosError.response) {
                event.contexts.axios_response = {
                  status: axiosError.response.status,
                  statusText: axiosError.response.statusText,
                  headers: axiosError.response.headers,
                };
              }
            }
          }

          return event;
        },
        denyUrls: [
          // Chrome extensions
          /extensions\//i,
          /^chrome:\/\//i,
          // Clarity
          /clarity\.ms/,
          /www\.clarity\.ms/,
          /https:\/\/www\.clarity\.ms\//,
        ],
      });
      setIsSentryLoaded(true);
    }
    if (user?.uid && merchant?.merchantId) {
      setUser({
        id: user?.uid,
        email: user?.email,
        username: user?.email,
        phone: user?.phone,
      });
      setTags({
        "user.id": user?.uid,
        "user.email": user?.email,
        "user.name": user?.name,
        "merchant.name": merchant?.displayName,
        "merchant.id": merchant?.merchantId,
      });
    }
    // if (user) {
    //   Sentry.setUser({
    //     id: user.userUuid,
    //     email: user.email,
    //     username: user.email,
    //   });
    //   Sentry.setTag("organization", user.organizationUuid);
    // }
  }, [isSentryLoaded, setIsSentryLoaded, isLoggingEnabled, user?.uid, merchant?.merchantId]);
};

export default useSentry;
